'use client';

import { SingleStoreType } from '@workspace/interfaces/store-locator';
import { Typography } from '@workspace/ui/common';
import { AlgoliaClearRefinements, NoResultsBoundary } from '../AlgoliaSearch';
import { useHits } from 'react-instantsearch';
import { Key, useCallback, useEffect, useRef } from 'react';
import { Hit as AlgoliaHit } from 'instantsearch.js/es/types';
import { StoreCard } from './StoreCard';
import { cn } from '@workspace/lib';
import { AutoSizer, List } from 'react-virtualized';
import 'react-virtualized/styles.css';

type Props = {
  selectedKey: string | null;
  onSelectStoreAction: (store: SingleStoreType) => void;
  directionLabel: string | null | undefined;
  loading?: boolean;
  notfoundHeading?: string;
  notfoundMessage?: string;
  clearInput?: () => void;
};

export default function ListStoreAIS({
  selectedKey,
  onSelectStoreAction,
  directionLabel,
  notfoundHeading,
  notfoundMessage,
  clearInput,
}: Props) {
  const { items: hits, sendEvent } = useHits<SingleStoreType>();
  const listRef = useRef<List>(null);

  // Scroll to top when search results change
  useEffect(() => {
    if (selectedKey) return;
    if (listRef.current) {
      listRef.current.scrollToRow(0);
    }
  }, [hits, selectedKey]);

  const handleStoreClick = useCallback(
    (hit: AlgoliaHit<SingleStoreType>) => {
      // Track click event for Algolia analytics using sendEvent
      try {
        sendEvent('click', hit, 'Hit Clicked'); // Default hit clicked event name
      } catch (error) {
        console.warn('Failed to track store click analytics:', error);
      }
      onSelectStoreAction(hit);
    },
    [sendEvent, onSelectStoreAction],
  );

  const renderRow = useCallback(
    ({ index, style, key }: { index: number; style: React.CSSProperties; key: Key }) => {
      const hit = hits[index];
      const storeKey = hit.objectID || '';
      const isSelected = selectedKey === storeKey;

      return (
        <div style={style} key={key}>
          <div onClick={() => handleStoreClick(hit)}>
            <StoreCard
              className={cn(
                'cursor-pointer rounded-md p-4 lg:px-lg lg:py-md h-49.5',
                isSelected ? 'border-2 border-secondary-300' : 'border border-secondary-100',
              )}
              storeCard={hit}
              directionLabel={directionLabel}
            />
          </div>
        </div>
      );
    },
    [hits, selectedKey, handleStoreClick, directionLabel],
  );

  return (
    <>
      <NoResultsBoundary
        fallback={
          <div className="flex flex-col items-center gap-sm">
            <Typography as="p" variant="h6" className="leading-6.5">
              {notfoundHeading}
            </Typography>
            <Typography variant="bodyMd">{notfoundMessage}</Typography>
            <AlgoliaClearRefinements
              className="pt-0 md:pt-0"
              overrideDisabled={false}
              onClick={clearInput}
              disableButtonClass="!hidden"
              translations={{ resetButtonText: 'Clear filters' }}
            />
          </div>
        }
      >
        <div className="h-full">
          <AutoSizer>
            {({ height, width }) => (
              <List
                ref={listRef}
                height={height}
                width={width}
                rowCount={hits.length}
                rowHeight={214} // Fixed height: StoreCard (~198px) + margin (16px)
                rowRenderer={renderRow}
                className="scrollbar-primary pr-sm max-md:pl-md md:pr-sm"
              />
            )}
          </AutoSizer>
        </div>
      </NoResultsBoundary>
    </>
  );
}
